import React, { useState, useMemo } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Collapse,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Print,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { fetchCrimeByNumber, clearSelectedCrime } from "../features/crimeSlice";
import { closeSnackbar, useSnackbar } from "notistack";
import DynamicBreadcrumbs from "../components/layout/Breadcrumb";
import Layout from "../components/layout/Layout";

const CrimeSearch = () => {
  const [crimeNumber, setCrimeNumber] = useState("");
  const [inputError, setInputError] = useState("");
  const [expanded, setExpanded] = useState({}); // For collapsing sections per incident

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { error, selectedCrime, status } = useSelector((state) => state.crime);
  const isLoading = status === "loading";

  // Safe handling: selectedCrime is array of incidents
  const incidents = Array.isArray(selectedCrime) ? selectedCrime : [];

  const hasResults = incidents.length > 0;

  // Validate crime number
  const validateCrimeNumber = (value) => {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) {
      setInputError("Crime number is required");
      return false;
    }
    if (!trimmed.startsWith("UPPCR-")) {
      setInputError("Crime number must start with UPPCR-");
      return false;
    }
    if (trimmed.length < 10) {
      setInputError("Crime number is too short");
      return false;
    }
    setInputError("");
    return true;
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const normalized = crimeNumber.trim().toUpperCase();
    if (!validateCrimeNumber(normalized)) return;

    try {
      await dispatch(fetchCrimeByNumber(normalized)).unwrap();
      enqueueSnackbar("Incident(s) loaded successfully", {
        variant: "success",
        autoHideDuration: 4000,
      });
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to fetch records", {
        variant: "error",
        autoHideDuration: 6000,
        action: (key) => (
          <IconButton size="small" onClick={() => closeSnackbar(key)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      });
    }
  };

  const handleClear = () => {
    setCrimeNumber("");
    setInputError("");
    dispatch(clearSelectedCrime());
  };

  const handleChange = (e) => {
    const value = e.target.value.toUpperCase();
    setCrimeNumber(value);
    if (value.trim()) validateCrimeNumber(value);
    else setInputError("");
  };

  // Count total status across all offenders in all incidents
  const statusCounts = useMemo(() => {
    const counts = {
      pending: 0,
      inCourt: 0,
      trial: 0,
      atPoliceStation: 0,
      clear: 0,
    };

    incidents.forEach((incident) => {
      incident.offenders?.forEach((offender) => {
        const status = offender.offenderStatus || "pending";
        if (counts[status] !== undefined) counts[status]++;
      });
    });

    return counts;
  }, [incidents]);

  const statusColors = {
    pending: "default",
    inCourt: "error",
    trial: "warning",
    atPoliceStation: "info",
    clear: "success",
  };

  const toggleExpand = (index) => {
    setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handlePrint = (incidentId) => {
  const printAreaId = `print_incident_${incidentId}`;
  const printContent = document.getElementById(printAreaId)?.innerHTML;

  if (!printContent) {
    alert('No content found to print. Please expand the incident first.');
    return;
  }

  // Open new window for printing
  const printWindow = window.open('', '_blank', 'left=100,top=100,width=900,height=800,toolbar=0,scrollbars=1,status=0');

  if (!printWindow) {
    alert('Pop-up blocked! Please allow pop-ups for this site and try again.');
    return;
  }

  // Write clean, styled HTML document
  printWindow.document.write(`
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <title>UPPCR - Incident Report ${incidentId}</title>
      <style>
        @page {
          size: A4;
          margin: 1.5cm;
        }
        body {
          font-family: 'Arial', 'Helvetica', sans-serif;
          font-size: 12pt;
          line-height: 1.5;
          color: #333;
          margin: 0;
          padding: 0;
        }
        .header {
          text-align: center;
          border-bottom: 2px solid #d32f2f;
          padding-bottom: 10px;
          margin-bottom: 20px;
        }
        .header h1 {
          margin: 0;
          color: #d32f2f;
          font-size: 22pt;
        }
        .header p {
          margin: 5px 0 0;
          font-size: 11pt;
          color: #555;
        }
        .content {
          padding: 10px;
        }
        h5, h6 {
          color: #d32f2f;
          margin-top: 1.2em;
          border-bottom: 1px solid #eee;
          padding-bottom: 5px;
        }
        table, .MuiPaper-root {
          width: 100%;
          border-collapse: collapse;
          margin: 15px 0;
        }
        th, td {
          border: 1px solid #ccc;
          padding: 8px;
          text-align: left;
        }
        th {
          background-color: #f5f5f5;
          font-weight: bold;
        }
        .chip {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 11pt;
          margin: 5px 0;
        }
        .footer {
          margin-top: 40px;
          text-align: center;
          font-size: 10pt;
          color: #777;
          border-top: 1px solid #ddd;
          padding-top: 15px;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Uttar Pradesh Police Crime Records (UPPCR)</h1>
        <p>Incident Report • Generated on ${new Date().toLocaleString('en-IN')}</p>
      </div>

      <div class="content">
        ${printContent}
      </div>

      <div class="footer">
        Confidential - For Official Use Only • Uttar Pradesh Police
      </div>

      <script>
        window.onload = function() {
          setTimeout(function() {
            window.focus();
            window.print();
            setTimeout(function() {
              window.close();
            }, 1000);
          }, 500);
        };
      </script>
    </body>
    </html>
  `);

  printWindow.document.close();
};

  return (
    <Layout
      title="Crime Search"
      description="Search Uttar Pradesh Police Crime Records"
    >
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <DynamicBreadcrumbs />

        <Paper
          elevation={4}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            background: "linear-gradient(145deg, #f8f9fa 0%, #ffffff 100%)",
          }}
        >
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{ fontWeight: 700, color: "#d32f2f" }}
          >
            UPPCR - Search by Crime Number
          </Typography>

          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            Enter the unique offender crime number to view all related incidents
            and details.
          </Typography>

          <Box component="form" onSubmit={handleSearch} noValidate>
            <TextField
              fullWidth
              label="Crime Number (UPPCR-XXXXXX)"
              placeholder="UPPCR-TS627711"
              value={crimeNumber}
              onChange={handleChange}
              error={!!inputError}
              helperText={inputError || " "}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: crimeNumber && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClear}>
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
              disabled={isLoading}
              inputProps={{ maxLength: 20 }}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={isLoading || !!inputError || !crimeNumber.trim()}
              sx={{
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress
                    size={24}
                    color="inherit"
                    sx={{ mr: 1.5 }}
                  />
                  Searching...
                </>
              ) : (
                "Search Incidents"
              )}
            </Button>
          </Box>

          {/* Server error */}
          {error && !hasResults && (
            <Alert
              severity="error"
              sx={{ mt: 3 }}
              onClose={() => dispatch(clearSelectedCrime?.())}
            >
              {error}
            </Alert>
          )}

          {/* Results */}
          {hasResults && (
            <Box mt={5}>
              {/* Status Summary */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  justifyContent: "center",
                  mb: 4,
                }}
              >
                {Object.entries(statusCounts).map(([status, count]) =>
                  count > 0 ? (
                    <Chip
                      key={status}
                      label={`${status
                        .replace(/([A-Z])/g, " $1")
                        .trim()}: ${count}`}
                      color={statusColors[status]}
                      size="medium"
                      variant="outlined"
                      sx={{ fontWeight: 600, minWidth: 140 }}
                    />
                  ) : null
                )}
              </Box>

              <Typography
                variant="h6"
                align="center"
                gutterBottom
                sx={{ mb: 4 }}
              >
                Found {incidents.length} incident
                {incidents.length > 1 ? "s" : ""} involving this offender
              </Typography>

              <Grid container spacing={3}>
                {incidents.map((incident, index) => (
                  <Grid item xs={12} key={incident._id}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        transition: "all 0.2s",
                        "&:hover": { boxShadow: 6 },
                      }}
                    >
                      {/* Incident Header */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          variant="h5"
                          fontWeight="bold"
                          color="primary.dark"
                        >
                          {incident.incidentId}
                        </Typography>
                        {expanded[index] && (
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handlePrint(incident.incidentId)}
                          sx={{ mr: 1 }}
                          title="Print this incident report"
                        >
                          <Print fontSize="small" />
                        </IconButton>
                      )}
                        <IconButton onClick={() => toggleExpand(index)}>
                          {expanded[index] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Basic Info */}
                      <div id={expanded[index] ? `print_incident_${incident.incidentId}` : undefined}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1">
                            <strong>Crime Type:</strong> {incident.crimeType}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1">
                            <strong>Date:</strong>{" "}
                            {new Date(incident.crimeDate).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              }
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1">
                            <strong>Location:</strong> {incident.district},{" "}
                            {incident.tehsil}, {incident.villageTown} | Police
                            Station: {incident.policeStation}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Collapse in={expanded[index] || false}>
                        <Box mt={3}>
                          {/* Offenders */}
                          <Typography variant="h6" gutterBottom>
                            Offenders ({incident.totalOffenders})
                          </Typography>
                          <Grid container spacing={2}>
                            {incident.offenders?.map((off, i) => (
                              <Grid item xs={12} sm={6} key={i}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                  >
                                    {off.personId?.firstName}{" "}
                                    {off.personId?.middleName}{" "}
                                    {off.personId?.lastName}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Crime Number:{" "}
                                    <strong>{off.crimeNumber===crimeNumber?crimeNumber:"Not Matched"}</strong>
                                  </Typography>
                                  <Typography variant="body2">
                                    Role:{" "}
                                    <strong>{off.role || "Offender"}</strong>
                                  </Typography>
                                  <Chip
                                    label={off.offenderStatus || "Pending"}
                                    color={
                                      statusColors[off.offenderStatus] ||
                                      "default"
                                    }
                                    size="small"
                                    sx={{ mt: 1 }}
                                  />
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>

                          {/* Victims & Witnesses */}
                          <Grid container spacing={3} mt={3}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="h6">
                                Victims ({incident.totalVictims})
                              </Typography>
                              {incident.victims?.length > 0 ? (
                                <List dense>
                                  {incident.victims.map((v, i) => (
                                    <ListItem key={i}>
                                      <ListItemText
                                        primary={v.name}
                                        secondary={`Mobile: ${
                                          v.mobile || "N/A"
                                        } • Aadhaar: ${v.aadhar || "N/A"}`}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              ) : (
                                <Typography color="text.secondary">
                                  No victims recorded
                                </Typography>
                              )}
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Typography variant="h6">
                                Witnesses ({incident.totalWitnesses})
                              </Typography>
                              {incident.eyeWitnesses?.length > 0 ? (
                                <List dense>
                                  {incident.eyeWitnesses.map((w, i) => (
                                    <ListItem key={i}>
                                      <ListItemText
                                        primary={w.name}
                                        secondary={`Mobile: ${
                                          w.mobile || "N/A"
                                        }`}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              ) : (
                                <Typography color="text.secondary">
                                  No witnesses recorded
                                </Typography>
                              )}
                            </Grid>
                          </Grid>

                          {/* Description & Reason */}
                          <Box mt={3}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Description
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ whiteSpace: "pre-wrap", mt: 1 }}
                            >
                              {incident.description || "Not provided"}
                            </Typography>

                            <Typography
                              variant="subtitle1"
                              fontWeight="bold"
                              mt={2}
                            >
                              Reason / Motive
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ whiteSpace: "pre-wrap" }}
                            >
                              {incident.reason || "Not specified"}
                            </Typography>
                          </Box>
                        </Box>
                      </Collapse>
                      </div>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Paper>
      </Container>
    </Layout>
  );
};

export default CrimeSearch;
